
import React, { useState } from 'react';

interface ValueCard {
  title: string;
  desc: string;
  icon: React.ReactNode;
  color: string;
  details: string[];
  cta: string;
}

const ValueProp: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const cards: ValueCard[] = [
    {
      title: "Automatización inteligente",
      desc: "Libera tiempo valioso eliminando tareas repetitivas mediante agentes de IA y flujos automáticos.",
      color: "text-blue-400",
      cta: "Optimizar procesos",
      details: [
        "Implementación de Agentes de IA para pre-calificación de leads.",
        "Automatización de flujos de trabajo entre apps (Zapier, Make, n8n).",
        "Procesamiento inteligente de documentos y facturación automática.",
        "Sistemas de alertas predictivas sobre desviaciones en el negocio."
      ],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Integración de sistemas",
      desc: "Hacemos que tus herramientas se hablen entre sí. Un ecosistema único para un control total.",
      color: "text-red-400",
      cta: "Unificar sistemas",
      details: [
        "Sincronización de stock real entre E-commerce y locales físicos.",
        "Centralización de WhatsApp API con tu sistema de gestión interna.",
        "Conexión de pasarelas de pago con reportes contables automáticos.",
        "Dashboards unificados que consolidan datos de múltiples fuentes."
      ],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      )
    },
    {
      title: "Consultoría tecnológica",
      desc: "No adivinamos, auditamos. Estrategia real aplicada a los problemas específicos de tu rubro.",
      color: "text-orange-400",
      cta: "Solicitar auditoría",
      details: [
        "Auditoría técnica de procesos actuales para detectar fugas de dinero.",
        "Diseño de Roadmap tecnológico para escalabilidad a 12-24 meses.",
        "Selección y optimización del stack tecnológico ideal para tu pyme.",
        "Acompañamiento estratégico en la adopción de nuevas tecnologías."
      ],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    }
  ];

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section id="soluciones" className="py-24 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Nuestra Propuesta
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Tecnología y estrategia que <br /> <span className="text-white">encajan con tu negocio</span></h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Automatizamos tareas, unificamos información y creamos plataformas a medida para que tengas control real de tu negocio, sin depender de procesos manuales lentos.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          {cards.map((card, idx) => {
            const isExpanded = expandedIndex === idx;
            return (
              <div 
                key={idx} 
                onClick={() => handleToggle(idx)}
                className={`group p-8 glass rounded-[2.5rem] border transition-all duration-500 cursor-pointer flex flex-col ${
                  isExpanded 
                    ? 'border-white/30 bg-white/10 scale-[1.02] shadow-[0_20px_50px_rgba(0,0,0,0.3)]' 
                    : 'border-white/5 hover:border-white/20 hover:translate-y-[-8px]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 transition-all duration-500 ${card.color} ${
                  isExpanded ? 'scale-110 bg-white/10' : 'group-hover:scale-110'
                }`}>
                  {card.icon}
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-white">{card.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm mb-6">
                  {card.desc}
                </p>

                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  isExpanded ? 'max-h-[400px] opacity-100 mb-8' : 'max-h-0 opacity-0'
                }`}>
                  <ul className="space-y-4 pt-6 border-t border-white/10">
                    {card.details.map((detail, dIdx) => (
                      <li key={dIdx} className="flex items-start gap-3 text-sm text-gray-300">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${card.color.replace('text-', 'bg-')}`} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <button className="mt-8 w-full py-3 bg-white text-black text-xs font-black rounded-xl hover:bg-gray-200 transition-colors uppercase tracking-widest">
                    {card.cta}
                  </button>
                </div>

                <div className={`mt-auto flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest transition-all ${
                  isExpanded ? 'opacity-0' : 'opacity-100'
                }`}>
                  Saber más 
                  <span className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all">
                    →
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValueProp;
